/**
import { createLogger } from '../logger';


const logger = createLogger('Lib - textArt');

 * @fileoverview Block-Style ASCII Art Text Generator
 * 
 * Converts input text to uppercase and renders it using block letter ASCII art.
 * Each letter is 5 lines tall. Supports A-Z and spaces. Unsupported characters
 * are rendered as blank spaces.
 * 
 * @module utils/textArt
 */

/**
 * Generates block-style ASCII art text from a string
 * 
 * Converts input text to uppercase and renders it using block letter ASCII art.
 * Each letter is 5 lines tall. Supports A-Z and spaces. Unsupported characters
 * are rendered as blank spaces.
 * 
 * @param text - The text string to convert to ASCII art
 * @returns Multi-line string containing block-style ASCII art representation
 * 
 * @example
 * ```ts
 * const art = generateTextArt('HELLO');
 * logger.info(art);
 * // Outputs:
 * // █   █ █████ █     █     █████ 
 * // █   █ █     █     █     █   █ 
 * // █████ ████  █     █     █   █ 
 * // █   █ █     █     █     █   █ 
 * // █   █ █████ █████ █████ █████ 
 * ```
 * 
 * @remarks
 * This is a minimal implementation for demonstration purposes.
 * For more advanced ASCII art features, consider integrating a library like figlet.
 */
export function generateTextArt(text: string): string {
  // Block letters mapping (A-Z)
  const alphabet: Record<string, string[]> = {
    A: ['  █  ', ' █ █ ', '█████', '█   █', '█   █'],
    B: ['████ ', '█   █', '████ ', '█   █', '████ '],
    C: [' ███ ', '█   █', '█    ', '█   █', ' ███ '],
    D: ['████ ', '█   █', '█   █', '█   █', '████ '],
    E: ['█████', '█    ', '████ ', '█    ', '█████'],
    F: ['█████', '█    ', '████ ', '█    ', '█    '],
    G: [' ███ ', '█    ', '█ ███', '█   █', ' ███ '],
    H: ['█   █', '█   █', '█████', '█   █', '█   █'],
    I: ['█████', '  █  ', '  █  ', '  █  ', '█████'],
    J: ['  ███', '   █ ', '   █ ', '█  █ ', ' ██  '],
    K: ['█   █', '█  █ ', '███  ', '█  █ ', '█   █'],
    L: ['█    ', '█    ', '█    ', '█    ', '█████'],
    M: ['█   █', '██ ██', '█ █ █', '█   █', '█   █'],
    N: ['█   █', '██  █', '█ █ █', '█  ██', '█   █'],
    O: [' ███ ', '█   █', '█   █', '█   █', ' ███ '],
    P: ['████ ', '█   █', '████ ', '█    ', '█    '],
    Q: [' ███ ', '█   █', '█   █', '█  ██', ' ████'],
    R: ['████ ', '█   █', '████ ', '█  █ ', '█   █'],
    S: [' ████', '█    ', ' ███ ', '    █', '████ '],
    T: ['█████', '  █  ', '  █  ', '  █  ', '  █  '],
    U: ['█   █', '█   █', '█   █', '█   █', ' ███ '],
    V: ['█   █', '█   █', '█   █', ' █ █ ', '  █  '],
    W: ['█   █', '█   █', '█ █ █', '██ ██', '█   █'],
    X: ['█   █', ' █ █ ', '  █  ', ' █ █ ', '█   █'],
    Y: ['█   █', ' █ █ ', '  █  ', '  █  ', '  █  '],
    Z: ['█████', '   █ ', '  █  ', ' █   ', '█████'],
    ' ': ['     ', '     ', '     ', '     ', '     '],
  };

  // Convert text to uppercase and map each character
  const lines = Array(5).fill('');
  for (const char of text.toUpperCase()) {
    const art = alphabet[char] || ['     ', '     ', '     ', '     ', '     '];
    for (let i = 0; i < lines.length; i++) {
      lines[i] += art[i] + ' ';
    }
  }
  return lines.join('\n');
}
